import { useEffect, useState, useCallback } from "react";
import { Plus, FileDown, FileText } from "lucide-react";
import { api, apiErr, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const REC_FIELDS = [
  ["purpose", "訓練目的", "ta"], ["scenario", "想定状況", "ta"], ["datetime_str", "日時"], ["place", "場所"],
  ["participants", "参加者"], ["roles", "役割分担", "ta"], ["content", "訓練内容", "ta"], ["duration", "所要時間"],
  ["problems", "問題点", "ta"], ["improvements", "改善事項", "ta"], ["responsible", "担当者"], ["deadline", "対応期限"], ["next_plan", "次回予定"],
];

export default function Drills() {
  const { fiscalYear } = useFiscalYear();
  const [items, setItems] = useState(null);
  const [offices, setOffices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", office_id: "", drill_type: "防災", scheduled_month: 4, scheduled_date: "", owner: "" });
  const [recOpen, setRecOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [rec, setRec] = useState({});

  const load = useCallback(() => {
    if (!fiscalYear) return;
    api.get(`/drills?fiscal_year=${fiscalYear}`).then(({ data }) => setItems(data));
  }, [fiscalYear]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);

  const create = async () => {
    if (!form.name || !form.office_id) { toast.error("訓練名と事業所は必須です"); return; }
    try { await api.post("/drills", { ...form, fiscal_year: fiscalYear }); toast.success("訓練を作成しました"); setOpen(false); setForm({ name: "", office_id: "", drill_type: "防災", scheduled_month: 4, scheduled_date: "", owner: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openRecord = async (d) => {
    setCurrent(d);
    const { data } = await api.get(`/drills/${d.id}/record`);
    setRec(data && data.drill_id ? data : { drill_id: d.id });
    setRecOpen(true);
  };

  const saveRecord = async () => {
    try { await api.post(`/drills/${current.id}/record`, { ...rec, drill_id: current.id }); toast.success("訓練記録を保存しました"); setRecOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const savePdf = async () => {
    await api.post(`/drills/${current.id}/record`, { ...rec, drill_id: current.id });
    const { data } = await api.get(`/drills/${current.id}/record`);
    if (data.id) openPdf(`/reports/drill/${data.id}`);
  };

  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader
        title="訓練" subtitle={`${fiscalYear}年度のBCP・防災・感染症訓練`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-drill-btn"><Plus className="w-4 h-4 mr-1" /> 訓練を作成</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>訓練を作成</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>訓練名 *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="drill-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>事業所 *</Label>
                    <Select value={form.office_id} onValueChange={(v) => setForm({ ...form, office_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="drill-office-select"><SelectValue placeholder="選択" /></SelectTrigger>
                      <SelectContent>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>種別</Label>
                    <Select value={form.drill_type} onValueChange={(v) => setForm({ ...form, drill_type: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="BCP">BCP</SelectItem><SelectItem value="防災">防災</SelectItem><SelectItem value="感染症">感染症</SelectItem><SelectItem value="その他">その他</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>予定月</Label><Input type="number" min="1" max="12" value={form.scheduled_month} onChange={(e) => setForm({ ...form, scheduled_month: Number(e.target.value) })} className="mt-1" /></div>
                  <div><Label>予定日</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="mt-1" /></div>
                </div>
                <div><Label>担当者</Label><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="mt-1" /></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-blue-600 hover:bg-blue-700" data-testid="save-drill-btn">作成</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {items.length ? items.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3 p-4" data-testid={`drill-row-${d.id}`}>
            <div>
              <div className="font-medium text-slate-800">{d.name} <span className="text-xs text-teal-600 ml-1">{d.drill_type}</span></div>
              <div className="text-xs text-slate-400 mt-0.5">予定：{d.scheduled_date || `${d.scheduled_month}月`}／担当：{d.owner || "-"}</div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={d.status} />
              {d.has_record && <span className="text-xs text-green-600 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> 記録あり</span>}
              <Button size="sm" variant="outline" onClick={() => openRecord(d)} data-testid={`record-btn-${d.id}`}>実施記録</Button>
            </div>
          </div>
        )) : <EmptyState title="訓練がありません" />}
      </div>

      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{current?.name} 実施記録</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {REC_FIELDS.map(([k, label, type]) => (
              <div key={k} className={type === "ta" ? "sm:col-span-2" : ""}>
                <Label>{label}</Label>
                {type === "ta"
                  ? <Textarea value={rec[k] || ""} onChange={(e) => setRec({ ...rec, [k]: e.target.value })} className="mt-1" data-testid={`rec-${k}`} />
                  : <Input value={rec[k] || ""} onChange={(e) => setRec({ ...rec, [k]: e.target.value })} className="mt-1" data-testid={`rec-${k}`} />}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={savePdf} data-testid="drill-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF出力</Button>
            <Button onClick={saveRecord} className="bg-blue-600 hover:bg-blue-700" data-testid="save-record-btn">保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
